import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, Edit3, Trash2, Lock, Unlock, Key } from 'lucide-react';
import EavList from '../components/EavList';
import UserSelect from '../components/UserSelect';
import { useTranslation } from '../context/LanguageContext';
import { usePermission } from '../context/PermissionContext';
import { useNotification } from '../context/NotificationContext';
import BatchUpdateModal from '../components/BatchUpdateModal';
import BatchAssignPositionModal from '../components/BatchAssignPositionModal';

const EmployeeList = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const { success: notifySuccess, error: notifyError } = useNotification();
  const [currentUser, setCurrentUser] = useState(null);
  const [loadedItems, setLoadedItems] = useState([]);
  const [batchUpdateIds, setBatchUpdateIds] = useState(null);
  const [batchAssignIds, setBatchAssignIds] = useState(null);

  // Load current user info (one-time on mount)
  useEffect(() => {
    fetch('/api/employees/current')
      .then(res => res.json())
      .then(data => {
        if (data.status === 'success') setCurrentUser(data.data);
      })
      .catch(err => console.error('Failed to fetch current user:', err));
  }, []);

  const flatColumns = [
    { code: 'entity_id', label: t('employees.id'), inputType: 'Text Field' },
    { code: 'email', label: t('employees.email'), inputType: 'Email' },
    { code: 'is_super_admin', label: t('employees.super_admin'), inputType: 'Yes/No' },
    { code: 'created_at', label: t('employees.created_at'), inputType: 'Date' },
    { code: 'hire_date', label: t('employees.hire_date'), inputType: 'Date' },
    { code: 'species', label: t('employees.species'), inputType: 'Dropdown' },
    { code: 'status', label: t('employees.status'), inputType: 'Dropdown' },
    { code: 'language', label: t('employees.language'), inputType: 'Text Field' },
    { code: 'attribute_set_id', label: t('products.attribute_set'), inputType: 'Text Field' },
    { code: 'updated_at', label: t('products.updated_at'), inputType: 'Date' },
    { code: 'created_by', label: t('products.created_by'), inputType: 'Text Field' },
  ];

  const filterCustomRenderers = {
    species: (col, value, onChange) => (
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white">
        <option value="">{t('common.all')}</option>
        <option value="human">{t('employees.human')}</option>
        <option value="ai">{t('employees.ai')}</option>
      </select>
    ),
    status: (col, value, onChange) => (
      <select value={value} onChange={e => onChange(e.target.value)}
        className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white">
        <option value="">{t('common.all')}</option>
        <option value="1">{t('employees.active')}</option>
        <option value="0">{t('employees.frozen')}</option>
      </select>
    ),
    created_by: (col, value, onChange) => (
      <UserSelect value={value} onChange={onChange} />
    ),
    created_at: (col, value, onChange, allFilters, setFilters) => (
      <div className="space-y-2">
        <input
          type="date"
          value={allFilters['created_at_from'] || ''}
          onChange={(e) => setFilters({...allFilters, created_at_from: e.target.value})}
          placeholder={t('common.from_date')}
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
        />
        <input
          type="date"
          value={allFilters['created_at_to'] || ''}
          onChange={(e) => setFilters({...allFilters, created_at_to: e.target.value})}
          placeholder={t('common.to_date')}
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
        />
      </div>
    ),
    updated_at: (col, value, onChange, allFilters, setFilters) => (
      <div className="space-y-2">
        <input
          type="date"
          value={allFilters['updated_at_from'] || ''}
          onChange={(e) => setFilters({...allFilters, updated_at_from: e.target.value})}
          placeholder={t('common.from_date')}
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
        />
        <input
          type="date"
          value={allFilters['updated_at_to'] || ''}
          onChange={(e) => setFilters({...allFilters, updated_at_to: e.target.value})}
          placeholder={t('common.to_date')}
          className="w-full p-2 border border-slate-300 dark:border-slate-600 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500 dark:bg-slate-800 dark:text-white"
        />
      </div>
    ),
  };

  // Permissions
  const canDeleteEmployee = useCallback((employee) => {
    if (!currentUser) return false;
    if (!hasPermission('employee', 'delete')) return false;
    if (!employee.is_super_admin) return true;
    return currentUser.is_super_admin === 1;
  }, [currentUser, hasPermission]);

  const canBatchDelete = useCallback((ids) => {
    if (!currentUser) return false;
    if (currentUser.is_super_admin === 1) return true;
    return !loadedItems
      .filter(e => ids.includes(e.entity_id))
      .some(e => e.is_super_admin === 1);
  }, [currentUser, loadedItems]);

  // Status toggle
  const handleToggleStatus = async (id, currentStatus, loadItems) => {
    const newStatus = currentStatus === 1 ? 0 : 1;
    const actionKey = currentStatus === 1 ? 'employees.freeze' : 'employees.unfreeze';
    if (!window.confirm(t('employees.confirm_toggle_status', { action: t(actionKey) }))) return;
    try {
      const res = await fetch('/api/employees/toggle-status', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, status: newStatus }),
      });
      const data = await res.json();
      if (data.status === 'success') {
        loadItems();
        notifySuccess(`${t(actionKey)}${t('common.success')}`);
      } else {
        notifyError(`${t(actionKey)}${t('common.failed')}: ${data.message}`);
      }
    } catch (err) {
      notifyError(`${t(actionKey)}${t('common.failed')}`);
    }
  };

  // ---- Custom actions ----
  const renderActions = (item, { handleDelete, loadItems }) => {
    const emp = loadedItems.find(e => e.entity_id === item.entity_id) || item;
    return (
      <>
        {hasPermission('employee', 'view') && (
          <button onClick={() => navigate(`/employees/view/${item.entity_id}`)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
            title={t('common.view')}><Eye size={16} /></button>
        )}
        {hasPermission('employee', 'edit') && (
          <button onClick={() => navigate(`/employees/edit/${item.entity_id}`)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
            title={t('common.edit')}><Edit3 size={16} /></button>
        )}
        {canDeleteEmployee(emp) && (
          <button onClick={() => handleDelete(item.entity_id)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
            title={t('common.delete')}><Trash2 size={16} /></button>
        )}
        {hasPermission('employee', 'freeze') && (emp.status === 1 ? (
          <button onClick={() => handleToggleStatus(item.entity_id, emp.status, loadItems)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 transition-colors"
            title={t('employees.freeze')}><Lock size={16} /></button>
        ) : (
          <button onClick={() => handleToggleStatus(item.entity_id, emp.status, loadItems)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
            title={t('employees.unfreeze')}><Unlock size={16} /></button>
        ))}
        {hasPermission('employee', 'tokens') && (
          <button onClick={() => navigate(`/tokens?employee_id=${item.entity_id}`)}
            className="p-1.5 rounded-lg text-slate-500 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 transition-colors"
            title={t('employees.tokens')}><Key size={16} /></button>
        )}
      </>
    );
  };

  return (
    <>
    <EavList
      entityType="employees"
      attributeSetId={3}
      flatColumns={flatColumns}
      defaultVisible={['entity_id', 'first_name', 'last_name', 'email', 'species', 'is_super_admin', 'status', 'created_at']}
      titleKey="employees.title"
      searchKey="employees.search_placeholder"
      pageTotalKey="products.page_total"
      confirmDeleteKey="employees.confirm_delete"
      confirmDeleteSingleKey="employees.confirm_delete_single"
      actionsLabelKey="products.actions"
      routes={{
        add: '/employees/new',
        view: (id) => `/employees/view/${id}`,
        edit: (id) => `/employees/edit/${id}`,
      }}
      deleteEndpoint={(id) => `/api/employees/${id}/delete`}
      usePageSizeChange={true}
      onBeforeDelete={(id) => {
        const emp = loadedItems.find(e => e.entity_id === id);
        if (emp && !canDeleteEmployee(emp)) {
          notifyError(t('employees.super_admin_delete_error'));
          return false;
        }
        return true;
      }}
      onBeforeBatchDelete={(ids) => {
        if (!canBatchDelete(ids)) {
          notifyError(t('employees.super_admin_delete_error'));
          return false;
        }
        return true;
      }}
      filterCustomRenderers={filterCustomRenderers}
      afterLoad={(items) => setLoadedItems(items)}
      renderActions={renderActions}
      onBatchUpdateCustom={setBatchUpdateIds}
      onBatchAssignPosition={hasPermission('employee', 'assign_position') ? setBatchAssignIds : undefined}
    />
      {batchUpdateIds && (
        <BatchUpdateModal
          entityType="employees"
          selectedIds={batchUpdateIds}
          onClose={() => setBatchUpdateIds(null)}
          onSuccess={() => {
            setBatchUpdateIds(null);
          }}
        />
      )}
      {batchAssignIds && (
        <BatchAssignPositionModal
          selectedIds={batchAssignIds}
          onClose={() => setBatchAssignIds(null)}
          onSuccess={() => {
            setBatchAssignIds(null);
          }}
        />
      )}
    </>
  );
};

export default EmployeeList;
